import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function EVChargerPage() {
  useEffect(() => {
    document.title = 'EV Charger Installation Watford | Electric Vehicle Charging Points | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional EV charger installation in Watford and surrounding areas. Government-approved installers for home and commercial electric vehicle charging points. Fast, safe, and certified installations.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'EV charger installation, electric vehicle charging, home EV charger, commercial EV charging, Watford electrician, OZEV approved installer');
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://beyondelectrical.co.uk/services/installations/ev-charger');
    }
  }, []);

  const chargerTypes = [
    {
      name: 'Home Chargers',
      power: '7kW - 22kW',
      chargingTime: '3-8 hours',
      icon: 'ri-home-4-line',
      features: ['Wall-mounted', 'Smart connectivity', 'Weatherproof', 'App control']
    },
    {
      name: 'Commercial Chargers',
      power: '22kW - 50kW',
      chargingTime: '1-4 hours',
      icon: 'ri-building-line',
      features: ['Multiple units', 'Payment systems', 'Load management', 'Remote monitoring']
    },
    {
      name: 'Rapid Chargers',
      power: '50kW+',
      chargingTime: '20-60 minutes',
      icon: 'ri-flashlight-line',
      features: ['DC fast charging', 'Multiple connectors', 'High capacity', 'Commercial grade']
    }
  ];

  const benefits = [
    {
      icon: 'ri-money-pound-circle-line',
      title: 'Cost Savings',
      description: 'Save up to 70% on fuel costs compared to petrol or diesel vehicles'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Eco-Friendly',
      description: 'Reduce your carbon footprint and contribute to a cleaner environment'
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Home Convenience',
      description: 'Charge overnight at home - wake up to a full battery every morning'
    },
    {
      icon: 'ri-funds-line',
      title: 'Property Value',
      description: 'Increase your property value with a professional EV charging installation'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Government Grants',
      description: 'Access available government grants and incentives for EV charger installations'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Smart Features',
      description: 'Monitor and control charging remotely via smartphone apps'
    }
  ];

  const installationProcess = [
    {
      step: '1',
      title: 'Site Survey',
      description: 'We assess your property and electrical system to determine the best charger location and type'
    },
    {
      step: '2',
      title: 'Quote & Planning',
      description: 'Receive a detailed quote and installation plan, including any necessary electrical upgrades'
    },
    {
      step: '3',
      title: 'Installation',
      description: 'Our certified engineers install your EV charger to the highest safety standards'
    },
    {
      step: '4',
      title: 'Testing & Handover',
      description: 'Complete testing and demonstration of your new charging point with full documentation'
    }
  ];

  const areas = [
    { name: 'Rickmansworth', url: '/services/installations/ev-charger/rickmansworth' },
    { name: 'Chorleywood', url: '/services/installations/ev-charger/chorleywood' },
    { name: 'Moor Park & Northwood', url: '/services/installations/ev-charger/moor-park-northwood' },
    { name: 'Croxley Green', url: '/services/installations/ev-charger/croxley-green' },
    { name: 'Pinner', url: '/services/installations/ev-charger/pinner' },
    { name: 'Bushey', url: '/services/installations/ev-charger/bushey' },
    { name: 'Abbots Langley', url: '/services/installations/ev-charger/abbots-langley' },
    { name: 'Harrow', url: '/services/installations/ev-charger/harrow' },
    { name: 'Ruislip', url: '/services/installations/ev-charger/ruislip' },
    { name: 'Stanmore', url: '/services/installations/ev-charger/stanmore' },
    { name: 'Edgware', url: '/services/installations/ev-charger/edgware' },
    { name: 'Mill Hill', url: '/services/installations/ev-charger/mill-hill' },
    { name: 'Radlett', url: '/services/installations/ev-charger/radlett' },
    { name: 'Borehamwood', url: '/services/installations/ev-charger/borehamwood' },
    { name: 'Elstree', url: '/services/installations/ev-charger/elstree' },
    { name: 'Potters Bar', url: '/services/installations/ev-charger/potters-bar' },
    { name: 'South Oxhey', url: '/services/installations/ev-charger/south-oxhey' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] to-[#0F2847] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1628] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-charging-pile-2-line"></i>
              Government Approved Installer
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Professional <span className="text-[#FFD700]">EV Charger</span> Installation
            </h1>
            
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Expert installation of electric vehicle charging points for homes and businesses across Watford and Hertfordshire. OZEV approved installers with full certification and warranty.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="tel:02031981818"
                className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call 020 3198 1818
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 text-white border-2 border-[#FFD700] px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Charger Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              EV Charger Types
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the right charging solution for your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {chargerTypes.map((charger, index) => (
              <div 
                key={index}
                className="bg-white border border-[#FFD700]/30 rounded-xl p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#FFD700] text-[#0A1628] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${charger.icon} text-3xl`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-[#0A1628] mb-2">{charger.name}</h3>
                <div className="text-[#FFD700] font-semibold mb-1">{charger.power}</div>
                <div className="text-gray-600 mb-6">Charging Time: {charger.chargingTime}</div>
                
                <ul className="space-y-3">
                  {charger.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-[#FFD700]"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#0F2847] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose EV Charging?
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              The benefits of installing an electric vehicle charging point
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-xl p-6 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#FFD700] text-[#0A1628] rounded-lg flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                <p className="text-gray-300 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Installation Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Simple, professional installation from start to finish
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {installationProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#FFD700] text-[#0A1628] rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Cover */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1628] mb-4">
              Areas We Cover
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional EV charger installation across Watford and surrounding areas
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {areas.map((area, index) => (
              <Link
                key={index}
                to={area.url}
                className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:bg-[#FFD700]/10 hover:border-[#FFD700] transition-all duration-300 group"
              >
                <i className="ri-map-pin-line text-2xl text-[#FFD700] mb-2 block"></i>
                <span className="text-[#0A1628] font-semibold group-hover:text-[#0A1628]">{area.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#0F2847] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Install Your EV Charger?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Get a free quote today and join the electric vehicle revolution
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="tel:02031981818"
              className="inline-flex items-center gap-2 bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              020 3198 1818
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white/10 text-white border-2 border-[#FFD700] px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
